/**
 * Utility functions for tribe detection and tracking.
 * Updated for the 6 sub-tribe system.
 */

import { Card, TribeId } from './data/cards/types';

/**
 * Detects the tribe of a card using the typed `tribe` field.
 * Returns null for neutral cards (no tribe).
 */
export function detectTribe(card: Card): TribeId | null {
  return card.tribe ?? null;
}

/**
 * How much a unit counts toward its tribe's breakpoints. Normally 1, but
 * Teniente Joven carries a `counts_as_two_for_tribe` passive and is worth
 * two bodies to the 2/4/6 thresholds without actually being two units.
 */
export function tribeWeight(card: Card): number {
  const ability = card.abilities?.find((a) => a.id === 'counts_as_two_for_tribe');
  if (!ability) return 1;
  return ability.params?.weight ?? 2;
}

/**
 * Counts how many units of each tribe are on a board, in breakpoint terms
 * (see `tribeWeight` — this is not always the same as a headcount).
 */
export function countTribesOnBoard(board: (Card | null)[]): Map<TribeId, number> {
  const counts = new Map<TribeId, number>();

  board.forEach(unit => {
    if (!unit) return;
    const tribe = detectTribe(unit);
    if (tribe) {
      counts.set(tribe, (counts.get(tribe) || 0) + tribeWeight(unit));
    }
  });

  return counts;
}

/**
 * Gets the player's most-represented tribe on their board.
 * Ties broken by highest max tier among the tribe's units.
 */
export function getHighestTierTribe(board: (Card | null)[]): TribeId | null {
  const tribeData = new Map<TribeId, { count: number; maxTier: number }>();

  board.forEach(unit => {
    if (!unit) return;
    const tribe = detectTribe(unit);
    if (tribe) {
      const current = tribeData.get(tribe) || { count: 0, maxTier: 0 };
      tribeData.set(tribe, {
        count: current.count + 1,
        maxTier: Math.max(current.maxTier, unit.tier)
      });
    }
  });

  if (tribeData.size === 0) return null;

  let bestTribe: TribeId | null = null;
  let bestTier = 0;

  tribeData.forEach((data, tribe) => {
    if (data.maxTier > bestTier) {
      bestTier = data.maxTier;
      bestTribe = tribe;
    }
  });

  return bestTribe;
}

/**
 * Gets all units of a specific tribe from a pool of cards.
 */
export function getUnitsOfTribe(
  cards: Card[],
  tribe: TribeId,
  maxTier?: number
): Card[] {
  return cards.filter(card => {
    const tierMatch = maxTier ? card.tier <= maxTier : true;
    return card.tribe === tribe && tierMatch;
  });
}
